import React, { useState } from "react";
import { FaCalendarAlt, FaBriefcaseMedical } from "react-icons/fa";
import {
  itemstableColumnsTrack,
} from "../../../constants/EmrContants";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../shared/CustomTable";
import CustomTabs from "../../../shared/CustomTabs";
import TrackingContent from "./TrackingContent";
import moment from "moment";

function ViewItems({
  visitData,
  embedded = false,
  disableDateGrouping = false,
  hideEmptyState = false
}) {
  const { t, i18n } = useTranslation();
  const [activeTabIndex, setActiveTabIndex] = useState(0);


  const isToday = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  
  const getActiveItems = () => {
    return (visitData?.items || []).filter(item => isToday(item?.created_at));
  };

  const getRowClassName = (row) => {
    const isPaid =
      row?.status?.toLowerCase() === "paid" ||
      row?.cash_invoice_number ||
      row?.pharmacy_invoice_number ||
      row?.insurance_invoice_number ||
      row?.government_invoice_number ||
      row?.corporate_invoice_number ||
      row?.invoice_numbers?.cash_invoice_number ||
      row?.invoice_numbers?.pharmacy_invoice_number ||
      row?.invoice_numbers?.insurance_invoice_number ||
      row?.invoice_numbers?.government_invoice_number ||
      row?.invoice_numbers?.corporate_invoice_number;
    
    return isPaid ? "[&>td]:!bg-green-100" : "";
  };

   const getOldOrdersData = () => {
    const dateGroups = {};
    
    (visitData?.items || []).forEach(item => {
      if (!isToday(item?.created_at)) {
        const dateKey = moment(item?.created_at).format('D MMMM YYYY');
        const sortKey = moment(item?.created_at).format('YYYY-MM-DD'); // For sorting
        if (!dateGroups[dateKey]) {
          dateGroups[dateKey] = {
            items: [],
            sortKey: sortKey
          };
        }
        dateGroups[dateKey].items.push(item);
      }
    });
    
    return dateGroups;
  };

  const activeItems = getActiveItems();
  const oldOrdersData = getOldOrdersData();
  const allItems = visitData?.items || [];

  const renderItemsTable = (items, emptyState) => {
    if (items.length > 0) {
      return (
        <CustomTable
          data={items}
          columns={itemstableColumnsTrack(t, i18n)}
          rowClassName={getRowClassName}
          rootyStyles={`overflow-auto border border-gray-200 rounded custom-table ${
            embedded ? "border-x-0 border-b-0 rounded-none" : ""
          }`}
        />
      );
    }

    if (hideEmptyState) return null;

    return (
      <div className="flex flex-col items-center justify-center py-12">
        {emptyState.icon}
        <span className="text-sm text-gray-500 font-medium">
          {emptyState.label}
        </span>
      </div>
    );
  };

  if (embedded) {
    if (disableDateGrouping) {
      return (
        <div className="bg-white">
          {renderItemsTable(allItems, {
            icon: <FaBriefcaseMedical size={40} className="text-gray-300 mb-3" />,
            label: t("No items found"),
          })}
        </div>
      );
    }

    return (
      <div className="bg-white">
        <div className="bg-white">
          {renderItemsTable(activeItems, {
            icon: <FaCalendarAlt size={40} className="text-gray-300 mb-3" />,
            label: t("No items ordered today"),
          })}
        </div>

        {Object.keys(oldOrdersData).length > 0 && (
          <div className="border-t border-gray-100 bg-white px-4 py-3">
            <div className="flex items-center gap-2 pb-3">
              <FaBriefcaseMedical size={12} className="text-primary" />
              <span className="text-[13px] font-semibold text-gray-800">
                {t("Old Orders")}
              </span>
              <span className="text-[11px] text-gray-500 font-medium">
                ({Object.keys(oldOrdersData).length})
              </span>
            </div>

            <div className="flex flex-col gap-4">
              {Object.entries(oldOrdersData)
                .sort(([, a], [, b]) => new Date(b.sortKey) - new Date(a.sortKey))
                .map(([date, data]) => (
                  <div key={date} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                      <FaCalendarAlt size={12} className="text-primary" />
                      <span className="text-[13px] font-semibold text-gray-800">
                        {date}
                      </span>
                      <span className="text-[11px] text-gray-500 font-medium">
                        ({data.items.length} {data.items.length === 1 ? t("item") : t("items")})
                      </span>
                    </div>
                    <CustomTable
                      data={data.items}
                      columns={itemstableColumnsTrack(t, i18n)}
                      rowClassName={getRowClassName}
                      rootyStyles="overflow-auto border border-gray-200 rounded custom-table"
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const tabs = [
    {
      name: `${t("Active")} (${activeItems.length})`,
      content: (
        <>
          {renderItemsTable(activeItems, {
            icon: <FaCalendarAlt size={40} className="text-gray-300 mb-3" />,
            label: t("No items ordered today"),
          })}
        </>
      ),
    },
    {
      name: `${t("Old Orders")} (${Object.keys(oldOrdersData).length})`,
      content: (
        <>
          {Object.keys(oldOrdersData).length > 0 ? (
            <div className="flex flex-col gap-4">
              {Object.entries(oldOrdersData)
                .sort(([, a], [, b]) => new Date(b.sortKey) - new Date(a.sortKey))
                .map(([date, data]) => (
                  <div key={date} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 pb-2 border-b-2 border-primary">
                      <FaCalendarAlt size={12} className="text-primary" />
                      <span className="text-[13px] font-semibold text-gray-800">
                        {date}
                      </span>
                      <span className="text-[11px] text-gray-500 font-medium">
                        ({data.items.length} {data.items.length === 1 ? t('item') : t('items')})
                      </span>
                    </div>
                    <CustomTable
                      data={data.items}
                      columns={itemstableColumnsTrack(t, i18n)}
                      rowClassName={getRowClassName}
                    />
                  </div>
                ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <FaBriefcaseMedical size={40} className="text-gray-300 mb-3" />
              <span className="text-sm text-gray-500 font-medium">{t("No old orders found")}</span>
            </div>
          )}
        </>
      ),
    },
    {
      name: t("Active History"),
      content: (
        <TrackingContent 
          trackType="visit" 
          trackId={visitData?.visit_id} 
          t={t} 
        />
      ),
    },
  ];

  return (
    <div className="max-h-[65vh] overflow-y-auto">
      <CustomTabs
        tabs={tabs}
        activeTabIndex={activeTabIndex}
        setActiveTabIndex={setActiveTabIndex}
      />
    </div>
  );
}

export default ViewItems;
